// The full-screen country picker's list logic (components/connect/CountryPicker),
// kept pure so it is tested on its own. Founder, 2026-10-19 (1.5.2 gallery
// .claude/1.5.2-connect-bank-fullscreen.html): the talent's country already
// picked for them, popular countries under it, and a search over every country.
import type { SupportedCountry } from "./supportedCountries";

/** The founder's popular list, in his order (2026-10-19 mockup). */
export const POPULAR_CODES = ["US", "CA", "GB", "FR", "ES", "DE", "AU"];

/** Lowercase, accents off, punctuation as spaces: "Côte d'Ivoire" → "cote d ivoire". */
export const normalize = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Other names people type for a country (English list names stay the source).
const ALIASES: Record<string, string[]> = {
  US: ["usa", "america", "united states of america"],
  GB: ["uk", "britain", "great britain", "england", "scotland", "wales", "northern ireland"],
  AE: ["uae", "emirates", "dubai"],
  KR: ["korea", "south korea"],
  CZ: ["czech republic"],
  NL: ["holland"],
  CI: ["ivory coast"],
  TR: ["turkey", "turkiye"],
  MM: ["burma"],
  TL: ["east timor"],
  SZ: ["swaziland"],
  MK: ["macedonia"],
  CV: ["cape verde", "cabo verde"],
  CD: ["congo", "drc"],
  CG: ["congo"],
  DE: ["deutschland"],
  ES: ["espana"],
};

/**
 * Every country matching `query`, best first: the name starts with it, then a
 * word of the name does, then another name for it does, then the name holds
 * it anywhere. A–Z inside each group. Empty query → nothing (the picker shows
 * its suggested + popular + A–Z lists instead).
 */
export function searchCountries(list: SupportedCountry[], query: string): SupportedCountry[] {
  const q = normalize(query);
  if (!q) return [];
  const rank = (c: SupportedCountry): number => {
    const name = normalize(c.name);
    if (name.startsWith(q)) return 0;
    if (name.split(" ").some((w) => w.startsWith(q))) return 1;
    if ((ALIASES[c.code] || []).some((a) => a.startsWith(q))) return 2;
    if (name.includes(q)) return 3;
    return -1;
  };
  return list
    .map((c) => ({ c, r: rank(c) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.c.name.localeCompare(b.c.name, "en"))
    .map((x) => x.c);
}

/** The popular countries the list has, in the founder's order, minus `exclude` (the selected one sits above). */
export function popularCountries(list: SupportedCountry[], exclude?: string | null): SupportedCountry[] {
  const byCode = new Map(list.map((c) => [c.code, c]));
  return POPULAR_CODES.filter((code) => code !== exclude)
    .map((code) => byCode.get(code))
    .filter((c): c is SupportedCountry => !!c);
}
